import { useTranslation } from 'react-i18next'

import { formatDate } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'

import {
  firstName,
  getComposerBanner,
  getInlineComposerBanner,
} from '../lib/derive'
import type { ComposerBanner, CustomerDeliverability } from '../lib/derive'
import {
  useNumberHealth,
  useNumberHealthResolved,
} from '../model/number-health-context'

/**
 * The composer-block descriptor for a recipient, or `null` when SMS is
 * deliverable. Lets the composer swap its input for the banner (rather than
 * merely annotate it) when the number can't receive texts.
 */
export function useNumberHealthBanner(
  phone?: string | null,
): ComposerBanner | null {
  return getComposerBanner(useNumberHealth(phone))
}

/**
 * Composer banner + settled flag for a recipient, choosing the source the way
 * the backend intends: a resolved customer carries deliverability inline
 * (settled immediately, no lookup), and only a confirmed customer-less number
 * (`customer === null`) falls back to the batched number-health lookup.
 * `customer === undefined` means the thread is still resolving who the
 * counterparty is — the composer holds its placeholder rather than flash the
 * input.
 */
export function useDeliverabilityBanner({
  customer,
  optedOut = false,
  phone,
}: {
  customer: CustomerDeliverability | null | undefined
  optedOut?: boolean
  phone?: string | null
}): { banner: ComposerBanner | null; resolved: boolean } {
  const fallbackPhone = customer === null ? phone : null
  const fallbackBanner = useNumberHealthBanner(fallbackPhone)
  const fallbackResolved = useNumberHealthResolved(fallbackPhone)

  const inline = getInlineComposerBanner({ customer, optedOut })
  if (inline) return { banner: inline, resolved: true }
  if (customer) return { banner: null, resolved: true }
  if (customer === null) {
    return { banner: fallbackBanner, resolved: fallbackResolved }
  }
  return { banner: null, resolved: !phone }
}

/** i18n key suffix picking the named/anon and dated/dateless copy variant. */
function bannerVariant(banner: ComposerBanner, name: string): string {
  if (banner.kind === 'invalid') return ''
  if (banner.kind === 'optedOut' && !banner.iso) {
    return name ? '.namedNoDate' : '.anonNoDate'
  }
  return name ? '.named' : '.anon'
}

const TONE_CLASS = {
  warn: 'bg-amber-100 text-orange-700',
  bad: 'bg-red-50 text-red-500',
} as const

const DOT_CLASS = {
  warn: 'bg-orange-700',
  bad: 'bg-red-500',
} as const

/** Presentational banner — the tone-coloured pill with its dot and copy. */
export function NumberHealthBannerView({
  banner,
  contactName,
  className,
}: {
  banner: ComposerBanner
  contactName?: string
  className?: string
}) {
  const { t } = useTranslation()
  const name = firstName(contactName)
  // Landline/opted-out copy addresses the contact by name; without one (e.g.
  // compose-new) we fall back to the impersonal variant. Inline opt-out flags
  // carry no date — those get the dateless copy.
  const variant = bannerVariant(banner, name)
  const copy = t(`numberHealth.banner.${banner.kind}${variant}`, {
    name,
    date: banner.iso ? formatDate(banner.iso) : '',
  })

  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-lg px-4 py-3 text-xs leading-relaxed',
        TONE_CLASS[banner.tone],
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'mt-1 size-2 shrink-0 rounded-full',
          DOT_CLASS[banner.tone],
        )}
      />
      <span className="min-w-0">{copy}</span>
    </div>
  )
}
